import { useCallback, useEffect, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import { offerQueryKeys, useGetOffer } from '~/hooks/apis/offers/useGetOffer';

import OffersService from '../../../../services/OffersService';
import { reportError } from '../../../../utils/reportError';

/**
 * Shared data-fetching and event-handler logic used by all schedule section
 * components (Schedules, LEDSchedules).  Component-specific concerns such as
 * delete-all / undo timers are intentionally left in each component.
 */
export function useScheduleSection({
  offerId,
  shouldMigrateSchedule,
  refreshToken,
}: {
  offerId: string;
  shouldMigrateSchedule: boolean;
  refreshToken?: number;
}) {
  const queryClient = useQueryClient();
  const { data: offerData, isLoading, isFetching, error } = useGetOffer(offerId);
  const schedules: Array<App.Schedule> = offerData?.brand_schedules ?? [];

  const [updateErrorMessages, setUpdateErrorMessages] = useState<Array<string> | null>(null);

  const invalidateSchedules = useCallback(() => {
    // Every create/update/delete writes a schedule log row server-side, so the schedule history
    // (CampaignScheduleHistoryCard) needs refreshing alongside the offer's brand_schedules.
    // The Salesforce import candidates' "already imported" match is also computed server-side
    // against the current brand_schedules, so a delete/create can flip a candidate's matched
    // status — without this it stays stale until a full page refresh.
    return Promise.all([
      queryClient.invalidateQueries({ queryKey: offerQueryKeys.detail(offerId) }),
      queryClient.invalidateQueries({ queryKey: offerQueryKeys.scheduleLogs(offerId) }),
      queryClient.invalidateQueries({ queryKey: offerQueryKeys.campaignScheduleImportCandidates(offerId) }),
    ]);
  }, [queryClient, offerId]);

  useEffect(() => {
    if (refreshToken) {
      invalidateSchedules();
    }
  }, [refreshToken]); // eslint-disable-line react-hooks/exhaustive-deps
  // `invalidateSchedules` is intentionally omitted: including it would cause a double-invalidation
  // on mount since it changes reference on initial render. `refreshToken` is the only intended trigger.

  const onSaveFinished = useCallback(() => {
    setUpdateErrorMessages(null);
    return invalidateSchedules();
  }, [invalidateSchedules]);

  const clearErrors = useCallback(() => setUpdateErrorMessages(null), []);

  const onError = useCallback(
    (err: Error) => {
      reportError(err);
      invalidateSchedules();
    },
    [invalidateSchedules],
  );

  const onUpdateErrors = useCallback((messages: Array<string>) => setUpdateErrorMessages(messages), []);

  const onDelete = useCallback(
    (id: number) => {
      if (shouldMigrateSchedule) {
        return;
      }
      queryClient.setQueryData(offerQueryKeys.detail(offerId), (old: App.Offer | undefined) => {
        if (!old) {
          return old;
        }
        return { ...old, brand_schedules: old.brand_schedules.filter((s) => s.id !== id) };
      });
      OffersService.deleteSchedule(offerId, id)
        .then(() => invalidateSchedules())
        .catch((e) => {
          onError(e);
          reportError(e);
        });
    },
    [offerId, shouldMigrateSchedule, queryClient, invalidateSchedules, onError],
  );

  return {
    schedules,
    isLoading,
    isFetching,
    error,
    updateErrorMessages,
    invalidateSchedules,
    onSaveFinished,
    clearErrors,
    onError,
    onUpdateErrors,
    onDelete,
    isRefetching: isFetching && !isLoading,
    showSchedules: !isLoading && !error,
    queryClient,
  };
}
